import packagejson from '../../package.json';
import { ServerInfo } from "./ServerInfo";
import { LoginManager } from "./LoginManager";
import { createRandomUUID } from "../helpers/Utilities";
import { ErrorLogger, ErrorSeverity } from "./errorLogger.service";

export interface APIMResponse {
  statusCode: "Y" | "N";
  messageCode: number;
  statusMessage: string;
  developerMessage: string | null;
  result?: any;
}

function APIMError(
  code: number,
  message: string,
  developer: string | null = null
): APIMResponse {
  return {
    statusCode: "N",
    messageCode: code,
    statusMessage: message,
    developerMessage: developer,
  };
}

export const APIMErrorNoCurrentUsername = APIMError(
  20001,
  "No current user name."
);
export const APIMErrorFilepathNotFound = APIMError(
  20002,
  "File path not found in attachment listing."
);

export const ApplicationSettings = {
  buildTeam: "AGENCYWEB",
  identifier: "AVIRPFG",
  userAgent: "Safari", // Placeholder until no longer needed
};

export function isTokenExpired(token: string): boolean {
  try {
    const payload = token.split(".")[1];
    if (!payload) return true;

    const decoded = JSON.parse(atob(payload));

    if (!decoded.exp) return false;

    const currentTime = Math.floor(Date.now() / 1000);
    const bufferTime = 60;

    return decoded.exp < currentTime + bufferTime;
  } catch (error) {
    console.error("Error decoding token:", error);
    ErrorLogger.get().logError(error, {
      component: 'APIMRequest',
      action: 'isTokenExpired',
      severity: ErrorSeverity.Warning
    });
    return true;
  }
}

export class APIMRequest {
  endpoint = "";
  method = "POST";
  body = "{}";
  contentType = "application/json";
  retriesRemaining = 1;

  /**
   * Resolves the full attachment URL from a documentLocation value.
   * Handles both legacy full URLs (strips any base URL prefix) and
   * new filename-only values by prepending the current environment attachmentUrl.
   */
  protected resolveAttachmentUrl(documentLocation: string, currentAttachmentUrl: string): string {
    const documentName = this.extractDocumentName(documentLocation);
    return currentAttachmentUrl + documentName;
  }

  protected extractDocumentName(documentLocation: string): string {
    try {
      const url = new URL(documentLocation);
      // Full URL: extract the last path segment (the filename)
      const pathSegments = url.pathname.split('/').filter(Boolean);
      return pathSegments.at(-1) || documentLocation;
    } catch {
      // Not a URL — already a filename
      return documentLocation;
    }
  }

  normalizeResponse(xhr: XMLHttpRequest): APIMResponse {
    const responseBody = xhr.response;

    if (responseBody === "") {
      return {
        statusCode: "N",
        messageCode: 2995831012,
        statusMessage: "No response from server.",
        developerMessage: null,
      };
    }

    if (responseBody === null) {
      return {
        statusCode: "N",
        messageCode: 2995831012,
        statusMessage: "Empty response from server.",
        developerMessage: null,
      };
    }

    if (xhr.status !== 200) {
      let parsedError: any = null;
      try {
        parsedError = JSON.parse(xhr.response);
      } catch {
        //Response body wasn't valid JSON
        parsedError = null;
      }
      return {
        statusCode: parsedError?.statusCode || "N",
        messageCode: parsedError?.messageCode || xhr.status,
        statusMessage: parsedError?.statusMessage || xhr.statusText,
        developerMessage: parsedError?.developerMessage || null,
      };
    }

    try {
      const responseJSON = JSON.parse(responseBody);

      if (responseJSON.statusCode === "Y" || responseJSON.statusCode === "N") {
        return responseJSON;
      }

      const numericStatusCode = Number.parseInt(responseJSON.statusCode, 10);
      const statusCode =
        Number.isNaN(numericStatusCode) || numericStatusCode === 0 ? "N" : "Y";

      return {
        statusCode,
        messageCode: numericStatusCode,
        statusMessage: responseJSON.statusMessage,
        developerMessage: responseJSON.developerMessage,
      };
    } catch {
      const original = "Original message: \n" + responseBody;
      return {
        statusCode: "N",
        messageCode: 2110219378,
        statusMessage: "Failed to process server response.",
        developerMessage: original,
      };
    }
  }

  send(): Promise<APIMResponse> {
    return new Promise((resolve, reject) => {
      ServerInfo.get()
        .then((environment) => {
          const loginManager = new LoginManager();
          const uid = loginManager.cwopa;

          if (uid == null) {
            reject(
              new Error(
                APIMErrorNoCurrentUsername.statusMessage ||
                "No current user name."
              )
            );
            return;
          }

          this.getValidAccessToken(loginManager, environment.oidcSettings.scope)
            .then((accessToken) =>
              this.makeRequest(accessToken, environment, uid, loginManager)
            )
            .then(resolve)
            .catch((error) => {
              console.error("APIMRequest send failed:", error);
              ErrorLogger.get().logError(error, {
                component: 'APIMRequest',
                action: 'send',
                severity: ErrorSeverity.Error,
                additionalData: {
                  endpoint: this.endpoint,
                  method: this.method
                }
              });
              const message =
                error instanceof Error ? error.message : "APIM request failed";
              reject(new Error(message));
            });
        })
        .catch((error) => {
          ErrorLogger.get().logError(error, {
            component: 'APIMRequest',
            action: 'getServerInfo',
            severity: ErrorSeverity.Error
          });
          reject(
            new Error(
              error instanceof Error
                ? "Failed to get server configuration"
                : "Unknown server configuration error"
            )
          );
        });
    });
  }

  private getValidAccessToken(
    loginManager: LoginManager,
    scope: string
  ): Promise<string> {
    return loginManager.getAccessToken(scope).then((accessToken) => {
      if (!isTokenExpired(accessToken)) {
        return accessToken;
      }
      return loginManager
        .refreshIdToken(scope)
        .then(() => {
          return loginManager.getAccessToken(scope);
        })
        .catch((error) => {
          console.error("Failed to refresh token:", error);
          ErrorLogger.get().logError(error, {
            component: 'APIMRequest',
            action: 'refreshToken',
            severity: ErrorSeverity.Warning,
            additionalData: { scope }
          });
          throw new Error(
            error instanceof Error
              ? `Token refresh failed: ${error.message}`
              : "Token refresh failed"
          );
        });
    });
  }

  private makeRequest(
    accessToken: string,
    environment: any,
    uid: string,
    loginManager: LoginManager
  ): Promise<APIMResponse> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = environment.serviceUrl + this.endpoint;

      xhr.open(this.method, url);
      xhr.setRequestHeader("Authorization", "Bearer " + accessToken);
      xhr.setRequestHeader("Content-Type", this.contentType);

      if (environment.apimSubscriptionKey) {
        xhr.setRequestHeader(
          "Ocp-Apim-Subscription-Key",
          environment.apimSubscriptionKey
        );
      }

      xhr.setRequestHeader("SourceApp", ApplicationSettings.identifier);
      xhr.setRequestHeader("userId", uid);
      xhr.setRequestHeader("X-App-Version", packagejson.version);
      xhr.setRequestHeader("X-Build-Team", ApplicationSettings.buildTeam);
      xhr.setRequestHeader("X-Request-UUID", createRandomUUID());
      xhr.setRequestHeader("X-Source-App", ApplicationSettings.identifier);
      xhr.setRequestHeader("X-User-ID", uid);

      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) {
          return;
        }

        try {
          const apimResponse = this.normalizeResponse(xhr);

          if (apimResponse.statusCode === "Y") {
            resolve(apimResponse);
            return;
          }

          if (apimResponse.messageCode === 401 && this.retriesRemaining > 0) {
            this.retriesRemaining -= 1;
            loginManager
              .refreshIdToken(environment.oidcSettings.scope)
              .then(() => this.send())
              .then(resolve)
              .catch((error) => {
                console.error("Error decoding token:", error);
                ErrorLogger.get().logError(error, {
                  component: 'APIMRequest',
                  action: 'retryAfter401',
                  severity: ErrorSeverity.Error,
                  additionalData: {
                    endpoint: this.endpoint,
                    retriesRemaining: this.retriesRemaining
                  }
                });
                const message =
                  error instanceof Error ? error.message : String(error);
                reject(new Error(message));
              });
          } else {
            reject(
              new Error(
                JSON.stringify(apimResponse as unknown as Record<string, unknown>)
              )
            );
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          reject(
            new Error(
              JSON.stringify({
                statusCode: "N",
                messageCode: 9999,
                statusMessage: message,
              } as APIMResponse)
            )
          );
        }
      };

      xhr.send(this.body);
    });
  }
}

export class APIMAPIGetRequest extends APIMRequest {
  constructor(endpoint: string) {
    super();
    this.endpoint = endpoint;
    this.method = "GET";
  }
}

export class APIMAPIPostRequest extends APIMRequest {
  constructor(endpoint: string, body: string) {
    super();
    this.body = body;
    this.endpoint = endpoint;
    this.method = "POST";
  }
}

export class APIMAPIPutRequest extends APIMRequest {
  constructor(endpoint: string, body: string) {
    super();
    this.body = body;
    this.endpoint = endpoint;
    this.method = "PUT";
  }
}

export class APIMAPIDeleteRequest extends APIMRequest {
  constructor(endpoint: string) {
    super();
    this.endpoint = endpoint;
    this.method = "DELETE";
  }
}
